import React                   from 'react';
import { observer }            from 'mobx-react-lite';
import debounce                from 'lodash/debounce';
import { SearchOutlined }      from '@ant-design/icons';
import View                    from '@widesk-ui/components/View';
import Input                   from '@widesk-ui/components/form/Input';
import Spin                    from '@widesk-ui/components/Spin';
import Segmented               from '@widesk-ui/Segmented';
import PagedCollection         from '@widesk-core/models/PagedCollection';
import PlaceModel              from '@models/map/PlaceModel';
import fileStore               from '@/stores/fileStore';
import useDesignTokens         from '@/hooks/useDesignTokens';
import placeShapesCount        from '@/TokamapEditor/helpers/placeShapesCount';
import storageImageCrossOrigin from '@/helpers/storageImageCrossOrigin';
import { corsImageSrc }        from '@/helpers/storageImageCrossOrigin';

const PAGE_SIZE = 20;
// Vignette de 36 px (la hauteur du nom et du type réunis) + 6 px de marge haut et bas + la bordure.
const ROW_HEIGHT = 50;
const ROW_GAP = 6;
// Pas d'une ligne à la suivante : chaque ligne a son cadre, séparé du suivant par `ROW_GAP`.
const ROW_STEP = ROW_HEIGHT + ROW_GAP;
const VISIBLE_ROWS = 5;
// « Non associés » se filtre ici (l'API ne connaît pas les formes) : une page peut n'en contenir aucun.
// On enchaîne alors les pages jusqu'à en avoir assez à montrer, dans cette limite par chargement.
const MAX_PAGES_PER_LOAD = 10;
const SEARCH_DELAY_MS = 300;

type Mode = 'unplaced' | 'all';

const hasNoShape = (place: PlaceModel) => {
	const { here, elsewhere } = placeShapesCount(String(place.id));

	return !here.length && !elsewhere;
};

/**
 * Liste cherchable et défilante de points d'intérêt, pour lier une forme à l'un d'eux. Les pages se
 * chargent au fil du défilement : elle tient sur un projet de plusieurs centaines de points
 * d'intérêt, là où une liste complète ne tiendrait pas. « Non associés » d'abord, le cas courant
 * quand on place les points d'intérêt un à un ; « Tous » pour retrouver sans son nom un point
 * d'intérêt déjà placé ailleurs.
 */
const PlacePicker = observer(({ projectId, onPick }: {
	projectId: id;
	onPick: (place: PlaceModel) => void;
}) => {
	const tokens = useDesignTokens();
	const [search, setSearch] = React.useState('');
	const [mode, setMode] = React.useState<Mode>('unplaced');
	const [places, setPlaces] = React.useState<PlaceModel[]>([]);
	const [total, setTotal] = React.useState<number>();
	const [loading, setLoading] = React.useState(true);
	const [active, setActive] = React.useState(0);
	const [complete, setComplete] = React.useState(false);

	const state = React.useRef({ page: 0, hasMore: true, query: '', mode: 'unplaced' as Mode, request: 0, busy: false });
	const listRef = React.useRef<HTMLDivElement>(null);

	const fetchPage = async (page: number, query: string) => {
		const collection = new PagedCollection(PlaceModel);

		collection
			.setItemsPerPage(PAGE_SIZE)
			.setPage(page)
			.setFilters({ project: projectId, ...(query ? { search: query } : {}) } as any)
			.setSort('label' as any, 'asc');

		await collection.list();

		const first = collection.first();
		if (first) await fileStore.fetchForModel(first, { fetchCollection: true });

		return collection;
	};

	const loadMore = async (reset = false) => {
		const current = state.current;

		if (!reset && (current.busy || !current.hasMore)) return;

		const request = reset ? ++current.request : current.request;

		if (reset) {
			current.page = 0;
			current.hasMore = true;
		}

		current.busy = true;
		setLoading(true);

		try {
			const added: PlaceModel[] = [];
			let pages = 0;

			while (current.hasMore && pages < MAX_PAGES_PER_LOAD && (added.length < VISIBLE_ROWS * 2 || current.mode === 'all' && !added.length)) {
				const collection = await fetchPage(current.page + 1, current.query);

				if (request !== current.request) return;

				current.page += 1;
				current.hasMore = collection.hasNextPage;
				pages += 1;

				if (current.page === 1) setTotal(collection.total);

				const models = collection.map(model => model as PlaceModel);

				added.push(...(current.mode === 'unplaced' ? models.filter(hasNoShape) : models));

				if (current.mode === 'all') break;
			}

			setPlaces(list => (reset ? added : list.concat(added)));
			setComplete(!current.hasMore);
			if (reset) {
				setActive(0);
				listRef.current?.scrollTo({ top: 0 });
			}
		} finally {
			if (request === current.request) {
				current.busy = false;
				setLoading(false);
			}
		}
	};

	const reload = React.useMemo(() => debounce(() => void loadMore(true), SEARCH_DELAY_MS), [projectId]);

	React.useEffect(() => {
		state.current.query = search.trim();
		state.current.mode = mode;
		setLoading(true);
		reload();
	}, [search, mode, reload]);

	React.useEffect(() => () => reload.cancel(), [reload]);

	const pick = (index: number) => {
		const place = places[index];

		if (place) onPick(place);
	};

	const moveActive = (step: 1 | -1) => {
		if (!places.length) return;

		const next = Math.min(places.length - 1, Math.max(0, active + step));

		setActive(next);

		const list = listRef.current;
		const top = next * ROW_STEP;

		if (list && (top < list.scrollTop || top + ROW_HEIGHT > list.scrollTop + list.clientHeight)) {
			list.scrollTo({ top: top - (step > 0 ? list.clientHeight - ROW_HEIGHT : 0) });
		}

		if (next >= places.length - 2) void loadMore();
	};

	const empty = !loading && !places.length;
	const searching = !!search.trim();

	return (
		<View gap={6}>
			<View row gap="xs" centerV>
				<View flex>
					<Input
						prefix={<SearchOutlined style={{ color: tokens.text.muted }} />}
						placeholder="Rechercher…"
						value={search}
						style={{ height: 34 }}
						// Hors d'un formulaire, l'`Input` de widesk transmet l'événement d'antd et non la valeur
						// annoncée par son type.
						onChange={(e: unknown) => setSearch((e as React.ChangeEvent<HTMLInputElement>).target?.value ?? String(e ?? ''))}
						onKeyDown={e => {
							if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
								e.preventDefault();
								moveActive(e.key === 'ArrowDown' ? 1 : -1);
							}

							if (e.key === 'Enter') {
								e.preventDefault();
								pick(active);
							}
						}}
					/>
				</View>

				<Segmented
					value={mode}
					onChange={value => setMode(value as Mode)}
					options={[
						{ value: 'unplaced', label: 'Non associés' },
						{ value: 'all', label: 'Tous' },
					]}
				/>
			</View>

			<div
				ref={listRef}
				role="listbox"
				onScroll={e => {
					const list = e.currentTarget;

					if (list.scrollTop + list.clientHeight >= list.scrollHeight - ROW_STEP * 2) void loadMore();
				}}
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: ROW_GAP,
					// Une ou deux lignes de plus que la limite ne valent pas une barre de défilement : tout
					// est montré quand il n'y a rien d'autre à charger.
					maxHeight: complete && places.length <= VISIBLE_ROWS + 2 ? undefined : ROW_STEP * VISIBLE_ROWS - ROW_GAP,
					overflowY: 'auto',
				}}
			>
				{places.map((place, index) => (
					<PlaceRow
						key={place.id}
						place={place}
						active={index === active}
						showUnplaced={mode === 'all'}
						onHover={() => setActive(index)}
						onPick={() => pick(index)}
					/>
				))}

				{/* Seulement pour un premier chargement, ou pour la suite quand la liste défile déjà : sous
				    quelques résultats, la ligne d'attente ajoutait de la hauteur et une barre inutile. */}
				{loading && (!places.length || places.length >= VISIBLE_ROWS) && (
					<View center py="sm"><Spin size="small" /></View>
				)}

				{empty && (
					<View py="xs" size={13} color={tokens.text.muted} gap={4}>
						{searching
							? 'Aucun résultat.'
							: mode === 'unplaced'
								? `Tous les ${PlaceModel.localize('plural', 'l')} sont déjà associés à une forme.`
								: `Aucun ${PlaceModel.localize('default', 'l')} pour l'instant.`}

						{mode === 'unplaced' && (
							<span>
								<a onClick={() => setMode('all')}>Voir tous les {PlaceModel.localize('plural', 'l')}</a>
							</span>
						)}
					</View>
				)}
			</div>

			{mode === 'all' && total !== undefined && !empty && (
				<View size={12} color={tokens.text.muted}>
					{total} {searching ? `résultat${total > 1 ? 's' : ''}` : PlaceModel.localize(total > 1 ? 'plural' : 'default', 'l')}
				</View>
			)}
		</View>
	);
});

const PlaceRow = observer(({ place, active, showUnplaced, onHover, onPick }: {
	place: PlaceModel;
	active: boolean;
	/** « Non associé » à droite d'un point d'intérêt sans forme — inutile quand la liste n'en montre pas d'autres. */
	showUnplaced: boolean;
	onHover: () => void;
	onPick: () => void;
}) => {
	const tokens = useDesignTokens();
	const image = fileStore.getFilesForModel(place).find(file => file.typeSource?.type?.reference === 'place_image');
	const src = image?.downloadPath;
	const placeType = place.getUrn('placeType') ? place.placeType : undefined;
	const { here, elsewhere } = placeShapesCount(String(place.id));
	const shapes = here.length + elsewhere;

	return (
		<div
			role="option"
			aria-selected={active}
			onMouseEnter={onHover}
			onClick={onPick}
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 10,
				flexShrink: 0,
				minHeight: ROW_HEIGHT,
				boxSizing: 'border-box',
				padding: '6px 10px',
				borderRadius: 8,
				cursor: 'pointer',
				border: `1px solid ${active ? `color-mix(in srgb, ${tokens.color.primary} 45%, ${tokens.border.subtle})` : tokens.border.subtle}`,
				background: active ? `color-mix(in srgb, ${tokens.color.primary} 5%, ${tokens.surface.elevated})` : tokens.surface.elevated,
				transition: 'border-color 0.15s, background-color 0.15s',
			}}
		>
			<View
				w={36}
				h={36}
				br={7}
				center
				style={{ flexShrink: 0, overflow: 'hidden', background: tokens.surface.subtle }}
			>
				{!!src && (
					<img
						src={corsImageSrc(src)}
						crossOrigin={storageImageCrossOrigin(src)}
						alt=""
						style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
					/>
				)}
			</View>

			<View flex gap={3} style={{ minWidth: 0 }}>
				<View size={14} lineHeight={1.3} style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
					{place.label}
				</View>

				{!!placeType?.label && (
					<View row centerV gap={5} size={11.5} lineHeight={1.3} color={tokens.text.muted}>
						<View w={7} h={7} br="50%" style={{ background: placeType.color || tokens.border.default, flexShrink: 0 }} />
						{placeType.label}
					</View>
				)}
			</View>

			{(shapes > 0 || showUnplaced) && (
				<View size={12} color={tokens.text.muted} style={{ flexShrink: 0 }}>
					{shapes > 0 ? `${shapes} forme${shapes > 1 ? 's' : ''}` : 'Non associé'}
				</View>
			)}
		</div>
	);
});

export default PlacePicker;
